import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Modal, Button } from "@components";
import { fetchCustomersV2, getCustomerLedgerTransactions } from "@api";
import { buildApiQuery, formatAmount, formatDate } from "@utils";
import RaiseQueryForm from "../../trade-accounts/components/RaiseQueryForm";
import SearchSelect from "./SearchSelect";
import { queryModalTitle } from "../common";

const searchCustomers = (term, opts) =>
  fetchCustomersV2(
    buildApiQuery({ page: 1, pageSize: 10, searchTerm: term, sort: "name", sortDirection: "asc" }),
    opts,
  ).then((r) => r?.data ?? []);

const NewQueryModal = ({ isOpen, onClose }) => {
  const queryClient = useQueryClient();
  const [step, setStep] = useState(1);
  const [customer, setCustomer] = useState(null);
  const [invoices, setInvoices] = useState([]);

  useEffect(() => {
    if (isOpen) return;
    setStep(1);
    setCustomer(null);
    setInvoices([]);
  }, [isOpen]);

  // Always refetch: a query raised or resolved elsewhere changes which invoices are open.
  const { data: ledger, isFetching: loadingInvoices } = useQuery({
    queryKey: ["new-query", "ledger", customer?.accountCode],
    queryFn: () => getCustomerLedgerTransactions(customer.accountCode),
    enabled: !!customer?.accountCode,
    staleTime: 0,
    refetchOnMount: "always",
  });

  const openInvoices = (ledger ?? []).filter(
    (t) => t.type === "Invoice" && t.remainingAmount !== 0 && !t.isUnderQuery,
  );

  const title = step === 1 ? "New query" : queryModalTitle(invoices);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      {step === 1 ? (
        <div className="flex flex-col gap-5 p-5 sm:p-6 bg-white">
          <SearchSelect
            label="Customer"
            placeholder="Search customers…"
            autoFocus
            value={customer}
            onSelect={(c) => {
              setCustomer(c);
              setInvoices([]);
            }}
            displayValue={(c) => (c.accountCode ? `${c.name} · ${c.accountCode}` : c.name)}
            getKey={(c) => c.id}
            renderOption={(c) => (
              <>
                <span className="text-sm text-zinc-900">{c.name}</span>
                {c.accountCode && <span className="text-xs text-zinc-500">{c.accountCode}</span>}
              </>
            )}
            search={searchCustomers}
            queryKey={["new-query", "customers"]}
            emptyText="No customers found"
          />

          {customer && (
            <SearchSelect
              label="Open invoices"
              placeholder="Search invoices…"
              multiple
              value={invoices}
              onSelect={setInvoices}
              displayValue={(i) => i.transactionNumber}
              getKey={(i) => i.id}
              sortBy={(i) => i.transactionNumber ?? ""}
              renderOption={(i) => (
                <>
                  <span className="text-sm text-zinc-900">{i.transactionNumber}</span>
                  <span className="text-xs text-zinc-500">
                    {formatAmount(i.remainingAmount)} · due {formatDate(i.dueDate)}
                  </span>
                </>
              )}
              options={openInvoices}
              filterOption={(i, term) =>
                (i.transactionNumber ?? "").toLowerCase().includes(term.toLowerCase())
              }
              emptyText={loadingInvoices ? "Loading invoices…" : "No open invoices"}
            />
          )}

          <div className="flex flex-col-reverse sm:flex-row gap-3 pt-1">
            <Button color="secondary" className="sm:flex-1" onClick={onClose}>
              Cancel
            </Button>
            <Button
              color="primary"
              className="sm:flex-1"
              disabled={!customer || invoices.length === 0}
              onClick={() => setStep(2)}
            >
              Next
            </Button>
          </div>
        </div>
      ) : (
        <RaiseQueryForm
          invoices={invoices}
          collectAssignee
          onCancel={() => setStep(1)}
          cancelLabel="Back"
          onDone={() => {
            queryClient.invalidateQueries({ queryKey: ["queries"] });
            queryClient.invalidateQueries({ queryKey: ["new-query", "ledger"] });
            onClose();
          }}
        />
      )}
    </Modal>
  );
};

export default NewQueryModal;
